/**
 * The sieve of Eratosthenes, played as a game of knocking things down.
 *
 * Tap 2 and every multiple of 2 falls away — but not 2 itself. Tap 3, then 5,
 * then 7, and what is still standing are the primes. The child is not told
 * what a prime is; they are left holding the numbers that nothing knocks over.
 */

export interface SieveState {
  limit: number;
  /** Numbers knocked out, by the number that knocked them. */
  struck: Map<number, number>;
  /** Which numbers have been tapped, in order. */
  used: number[];
}

export function createSieve(limit: number): SieveState {
  return { limit, struck: new Map(), used: [] };
}

/** Every multiple of p up to the limit, except p itself. */
export function multiplesOf(p: number, limit: number): number[] {
  const out: number[] = [];
  if (p < 2) return out;
  for (let n = p * 2; n <= limit; n += p) out.push(n);
  return out;
}

export function strike(state: SieveState, p: number): SieveState {
  if (p < 2 || p > state.limit || state.used.includes(p)) return state;
  const struck = new Map(state.struck);
  for (const n of multiplesOf(p, state.limit)) {
    if (!struck.has(n)) struck.set(n, p);
  }
  return { limit: state.limit, struck, used: [...state.used, p] };
}

export function isStanding(state: SieveState, n: number): boolean {
  return n >= 2 && n <= state.limit && !state.struck.has(n);
}

/**
 * The smallest number still standing that has not been used yet — what the
 * "next one" button reaches for, and the reason the game always terminates.
 */
export function nextToStrike(state: SieveState): number | null {
  for (let n = 2; n <= state.limit; n++) {
    if (isStanding(state, n) && !state.used.includes(n)) return n;
  }
  return null;
}

/** Sieving is finished once every prime up to sqrt(limit) has been used. */
export function isComplete(state: SieveState): boolean {
  const root = Math.floor(Math.sqrt(state.limit));
  for (let n = 2; n <= root; n++) {
    if (isStanding(state, n) && !state.used.includes(n)) return false;
  }
  return true;
}

/** Plain primality, used to check the game against the truth. */
export function isPrime(n: number): boolean {
  if (n < 2) return false;
  if (n % 2 === 0) return n === 2;
  const root = Math.floor(Math.sqrt(n));
  for (let d = 3; d <= root; d += 2) {
    if (n % d === 0) return false;
  }
  return true;
}
